import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';

import { Label } from '@components';
import { useContext } from '@hooks';
import { mergeClasses } from '@utils';

import { ProductCardContext } from './ProductCard';

/**
 * ProductCard.Image is a child component that displays the Product Image within a ProductCard component.
 * Must be used as a child component of the ProductCard.
 *
 * @component ProductCard.Image
 * @param {Object} props - The properties for the ProductCard.Image component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard.Image container.
 * @param {string} props.alt - The alternative text for the Product Image.
 * @param {string} props.src - The URL of the Product Image.
 * @returns {ReactElement} The rendered ProductCard.Image component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266712/fdf6817198ad.jpg" alt="Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6" />
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCardImage({
  className,
  alt,
  src,
  ...restProps
}) {
  const { isLimited, isPreorder } = useContext(
    ProductCardContext,
    'ProductCard.Image must be a child of ProductCard.'
  );

  return (
    <div className="relative">
      <div className="aspect-h-1 aspect-w-1">
        <img
          src={src}
          alt={alt}
          className={mergeClasses(
            'h-full w-full rounded-t-2xl object-cover object-center',
            className
          )}
          {...restProps}
        />
      </div>
      <div className="absolute inset-x-2 bottom-2 flex flex-wrap gap-0.5">
        {isPreorder && (
          <Label className="bg-gradient-to-r from-[#AA71E5] to-[#640CD4] text-white">
            {t`Preorder`}
          </Label>
        )}
        {isLimited && (
          <Label className="bg-black text-white">{t`Limited Edition`}</Label>
        )}
      </div>
    </div>
  );
}

ProductCardImage.propTypes = {
  className: PropTypes.string,
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired
};

ProductCardImage.defaultProps = {
  className: ''
};
